import World from './World';
import Renderer from './globals/Renderer';
import PerformanceProfile from './utils/PerformanceProfile';

class WebGL {
    domElement!: HTMLDivElement;
    renderer?: Renderer;
    world?: World;
    isFullScreen!: boolean;
    first: boolean;
    liquidIDReadyPromise: Promise<void>;
    readyPromise: Promise<void>;
    liquidIDResolve!: () => void;
    readyResolve!: () => void;
    devMode: boolean;

    constructor() {
        this.first = true;
        this.devMode = PerformanceProfile.devMode;

        // initPromises
        this.liquidIDReadyPromise = new Promise<void>(this.liquidIDCreateResolve.bind(this));
        this.readyPromise = new Promise<void>(this.readyCreateResolve.bind(this));
    }

    liquidIDCreateResolve(resolve) {
        this.liquidIDResolve = resolve;
    }

    readyCreateResolve(resolve) {
        this.readyResolve = resolve;
    }

    public setDOM(domElement, isFullScreen: boolean) {
        this.domElement = domElement;
        this.isFullScreen = isFullScreen;

        // Doesn't happens on first load, but new page
        if (this.renderer) {
            this.renderer.setParent(this.domElement);
            this.renderer.setFullScreen(this.domElement, isFullScreen);
        }
    }

    public async start(_showCube: boolean | undefined, _background: boolean, _logo: boolean, clearColor?: number) {
        if (this.first) {
            await PerformanceProfile.ready();

            this.renderer = Renderer.getInstance(this.domElement, this.isFullScreen);
            this.renderer.setClearColor(clearColor ? clearColor : 0x000000, 0);
            this.world = new World(this.domElement, true, this.devMode);

            this.renderer.setParent(this.domElement);
            this.renderer.setFullScreen(this.domElement, this.isFullScreen);

            this.first = false;
            this.world.start();
            this.readyResolve();
        }

        if (this.world) this.world.enableRendering();
    }

    async loadLiquidID() {
        await this.ready();
        await this.world?.loadLiquidID();
        this.liquidIDResolve();
    }

    public stop() {
        if (this.world) this.world.disableRendering();
    }

    // Listeners
    async liquidIDReady() {
        await this.liquidIDReadyPromise;
    }

    async ready() {
        await this.readyPromise;
    }

    updateLayer(id, domElement, options = {}) {
        this.world?.layerManager.updateLayer(id, domElement, options);
    }

    addCardLayer(id, domElement, options) {
        if (this.world?.layerManager[id]) {
            // TODO: CHECK IF LAYER CREATED ALREADY
            this.world.layerManager.layers[id].updateDomElement(domElement);
        } else {
            this.world?.newCardLayer(id, domElement, options);
        }
    }

    getLayer(id) {
        if (this.world?.layerManager.layers[id]) return this.world.layerManager.layers[id];
        else {
            const commonLayer = this.world?.checkCommonLayer(id);
            // eslint-disable-next-line no-console
            if (this.devMode && !commonLayer) console.warn(`⛔️ WebGL Layer "${id}" Not Found ⛔️`);
            return commonLayer;
        }
    }

    deleteLayer(id) {
        this.world?.layerManager.deleteLayer(id);
    }

    enableLayer(id) {
        this.world?.enableLayer(id);
    }

    disableLayer(id) {
        this.world?.disableLayer(id);
    }

    async observeNavigation(element) {
        await this.ready();
        this.world?.layerManager.observeNavigation(element);
    }

    // deleteCardLayer() {}
}

export default new WebGL();
